import { intToFormattedString, nullSafeToString } from '../lib/util';

/*
 * Checkout capability flags, merged from the three places the API keeps them:
 * GET /siteShippingConfigurations/v2, GET /shippingQuotes/defaults and GET /sites/v2/settings.
 * Consumers should not have to know which endpoint owns which flag.
 */

/**
 * The capabilities and defaults that govern what a checkout may offer.
 *
 * @property {boolean} blindShippingEnabled - (Read only) Whether blind shipping may be offered at checkout.
 * @property {boolean} customShippingMethodEnabled - (Read only) Whether the shopper may describe their own shipping service instead of choosing a rate.
 * @property {boolean} referenceNumbersEditable - (Read only) Whether shipping reference numbers may be edited.
 * @property {boolean} fedExReferenceNumbersEditable - (Read only) Whether the FedEx reference number may be edited.
 * @property {boolean} showSaturdayDelivery - (Read only) Whether the Saturday delivery option should be offered.
 * @property {boolean} signatureOnDeliveryEnabled - (Read only) Whether the signature on delivery option should be offered.
 * @property {boolean} dimensionalShippingEnabled - (Read only) Whether dimensional weight pricing applies.
 * @property {boolean} useUps - (Read only) Whether UPS is an enabled carrier for this site.
 * @property {boolean} useUsps - (Read only) Whether USPS is an enabled carrier for this site.
 * @property {boolean} useFedEx - (Read only) Whether FedEx is an enabled carrier for this site.
 * @property {?number} highShippingQuoteThreshold - (Read only) Shipping cost above which the shopper should be asked to contact support for a manual quote. Null if not set.
 * @property {string} displayHighShippingQuoteThreshold - (Read only) this.highShippingQuoteThreshold formatted with commas and two decimal places. Empty if not set.
 * @property {string} defaultReferenceNumber1 - (Read only) Default value for the first shipping reference number.
 * @property {string} defaultReferenceNumber2 - (Read only) Default value for the second shipping reference number.
 * @property {string} defaultFedExReferenceNumber - (Read only) Default value for the FedEx reference number.
 * @property {?number} defaultCountryId - (Read only) The country to preselect for shipping.
 * @property {string} defaultPostalCode - (Read only) The postal code to preselect for shipping.
 * @property {boolean} showPricing - (Read only) Whether this storefront shows pricing at all.
 * @property {boolean} orderApprovalEnabled - (Read only) Whether orders on this site route through approval.
 * @property {boolean} divisionOrderingEnabled - (Read only) Whether ordering on behalf of a division is enabled.
 * @property {boolean} allowCheckoutPaymentTypeOverride - (Read only) Whether the site permits overriding the default checkout payment type.
 */

class CheckoutSettings {
    constructor ({ shippingConfiguration = {}, shippingDefaults = {}, siteSettings = null } = {}) {
        const commerce = siteSettings ? siteSettings.commerce : {};
        const features = siteSettings ? siteSettings.features : {};
        const threshold = shippingConfiguration.highShippingQuoteThreshold;

        this.blindShippingEnabled = Boolean(shippingConfiguration.blindShippingAtCheckout);
        this.customShippingMethodEnabled = Boolean(shippingConfiguration.customMethodEnabled);
        this.referenceNumbersEditable = Boolean(shippingConfiguration.referenceNumbersEditable);
        this.fedExReferenceNumbersEditable = Boolean(shippingConfiguration.fedexReferenceNumbersEditable);
        this.showSaturdayDelivery = Boolean(shippingConfiguration.showSaturdayDelivery);
        this.signatureOnDeliveryEnabled = Boolean(shippingConfiguration.signatureOnDeliveryEnabled);
        this.dimensionalShippingEnabled = Boolean(shippingConfiguration.dimensionalShippingEnabled);
        this.useUps = Boolean(shippingConfiguration.useUps);
        this.useUsps = Boolean(shippingConfiguration.useUsps);
        this.useFedEx = Boolean(shippingConfiguration.useFedex);
        this.highShippingQuoteThreshold = typeof threshold === 'number' ? threshold : null;
        this.displayHighShippingQuoteThreshold = this.highShippingQuoteThreshold === null
            ? ''
            : intToFormattedString(this.highShippingQuoteThreshold.toFixed(2));

        // Defaults live on both the shipping configuration and the quote defaults endpoint; the quote
        // defaults are the ones the checkout rate call itself is seeded from, so they win.
        this.defaultReferenceNumber1 = nullSafeToString(shippingDefaults.defaultReferenceNumber1 || shippingConfiguration.defaultReferenceNumber1);
        this.defaultReferenceNumber2 = nullSafeToString(shippingDefaults.defaultReferenceNumber2 || shippingConfiguration.defaultReferenceNumber2);
        this.defaultFedExReferenceNumber = nullSafeToString(shippingDefaults.defaultFedExReferenceNumber || shippingConfiguration.fedexDefaultReferenceNumber);
        this.defaultCountryId = shippingDefaults.defaultCountryId || (commerce && commerce.defaultShipCountryId) || null;
        this.defaultPostalCode = nullSafeToString(shippingDefaults.defaultZipCode);

        this.showPricing = Boolean(commerce && commerce.showPricing);
        this.orderApprovalEnabled = Boolean(commerce && commerce.orderApprovalEnabled);
        this.allowCheckoutPaymentTypeOverride = Boolean(commerce && commerce.allowCheckoutPaymentTypeOverride);
        this.divisionOrderingEnabled = Boolean(features && features.divisionOrderingEnabled);

        Object.freeze(this);
    }
}

export default CheckoutSettings;
